import { useEffect, useState } from "react";
import api from "../services/api";
import { motion, AnimatePresence } from "framer-motion";
import { PlayCircle, Video, Calendar, Clock, Search, Film } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { getMyPurchases } from "../services/purchase";

export default function RecordingsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("student");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRole(decoded.role || "student");
      } catch { }
    }

    const init = async () => {
      try {
        const [res, purchaseRes] = await Promise.all([
          api.get("/live/my").catch(() => ({ data: [] })),
          getMyPurchases().catch(() => ({ data: [] }))
        ]);

        // 1. Map live recordings
        const filteredRecordings = (res.data || [])
          .filter(c => c.recording_url && c.recording_url.trim() !== "")
          .map(rec => ({
            id: `rec-${rec.id}`,
            type: "recording",
            title: "Recorded Session", // Fallback below
            courseId: rec.course_id,
            url: rec.recording_url,
            scheduledAt: rec.scheduled_at,
            image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop"
          }));

        // 2. Map purchased courses as continue learning videos
        const purchasedVideos = (purchaseRes.data || []).map(course => ({
          id: `course-${course.id}`,
          type: "course",
          title: course.title,
          courseId: course.id,
          image: course.thumbnail || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop",
          author: course.category || "General"
        }));

        // Fetch mapping titles if necessary
        const courseRes = await api.get("/courses").catch(() => ({ data: [] }));
        const map = Object.fromEntries(courseRes.data.map(c => [c.id, c.title]));

        const finalItems = [
          ...purchasedVideos,
          ...filteredRecordings.map(r => ({ ...r, title: map[r.courseId] || "Live Stream Archive" }))
        ];

        setItems(finalItems);
      } catch (err) {
        toast.error("Failed to aggregate video resources");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const filteredList = items.filter(item => {
    return item.title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="w-full">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-6 lg:p-10 space-y-10 min-w-0">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white flex items-center gap-3">
              <Film className="text-indigo-600" /> Recorded Videos
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Watch previous live sessions and on-demand content anytime.</p>
          </div>

          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by course..."
              className="pl-11 pr-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all w-full md:w-64 text-slate-800 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </header>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : filteredList.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 border border-dashed dark:border-slate-800 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
            <div className="w-20 h-20 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mb-6 text-indigo-500">
              <Video size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">No recordings available</h3>
            <p className="text-slate-500 text-sm mt-2 max-w-sm">Once your instructors conclude live streams, the recordings will automatically appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredList.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ y: -5 }}
                className="bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all group flex flex-col"
              >
                <div className="aspect-video bg-slate-900 relative flex items-center justify-center overflow-hidden">
                  <img src={item.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                  {item.type === "course" ? (
                    <button onClick={() => navigate(`/course/${item.courseId}`)} className="relative w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/40 text-white group-hover:scale-110 group-hover:bg-blue-600 group-hover:border-blue-500 transition-all shadow-xl">
                      <PlayCircle size={28} fill="currentColor" className="ml-0.5 text-white" />
                    </button>
                  ) : (
                    <a href={item.url} target="_blank" rel="noreferrer" className="relative w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/40 text-white group-hover:scale-110 group-hover:bg-indigo-600 group-hover:border-indigo-500 transition-all shadow-xl">
                      <PlayCircle size={28} fill="currentColor" className="ml-0.5 text-white" />
                    </a>
                  )}

                  <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                    <span className={`backdrop-blur-md text-white text-[10px] font-black px-2.5 py-1 rounded-lg tracking-wider flex items-center gap-1.5 uppercase shadow-lg ${item.type === "course" ? "bg-blue-600/80" : "bg-black/50"
                      }`}>
                      <Film size={10} /> {item.type === "course" ? "Continue Learning" : "Live Archive"}
                    </span>
                    {item.type === "recording" && item.scheduledAt && (
                      <span className="text-[10px] font-bold text-slate-200 flex items-center gap-1 bg-black/50 backdrop-blur-md px-2 py-1 rounded-md">
                        <Calendar size={10} /> {new Date(item.scheduledAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col cursor-pointer" onClick={() => item.type === "course" ? navigate(`/course/${item.courseId}`) : window.open(item.url, "_blank")}>
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white mb-2 line-clamp-2 leading-snug group-hover:text-indigo-600 transition-colors">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-4 text-slate-400 dark:text-slate-500 text-xs font-bold mt-auto">
                    {item.type === "course" ? (
                      <span className="text-blue-500 uppercase tracking-widest text-[10px]">{item.author}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        {item.scheduledAt ? new Date(item.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                      </div>
                    )}
                    <div className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
                    <span>HD Quality</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
